import Dexie, { Table } from 'dexie';
import { normalizeUrl } from '../chrome/normalizeUrl';
import * as _ from 'lodash';
import { shouldIgnore } from './urlNormalizationPrefs';
import { logger } from '../utils/logger';

export function getDBContext(): string {
  if (typeof window === 'undefined' || !window.document) {
    return '[SW]';
  }
  return '[UI]';
}

export function dbLog(message: string, ...args: any[]) {
  logger.info(`${getDBContext()} ${message}`, ...args);
}

export interface Pref {
  name: string;
  strVal?: string;
  numVal?: number;
  boolVal?: boolean;
  dirHandle?: FileSystemDirectoryHandle;
}

export interface DBPage {
  url: string; // normalized  // maybe todo://<uuid>
  originalUrl: string;
  domainSortKey: string;
  title?: string;
  favIconUrl?: string;
  visible: 'active' | 'suspended' | 'closed';
  accessTimes: number[];
  lastActiveDesc: number; // redundant with -accessTimes[-1], for indexing
  hotnessDesc: number; // -accessTimes.length, for indexing.  Note thinning emphasizes recent accesses.
  humanTags: string[]; // including "_stash_", "_todo_", "_flag_", ...
  humanRejectedTags: string[];
  autoTags: AutoTag[]; // TODO: add strengths
  openerUrls: string[]; // graph backward links
  openedUrls: string[]; // graph forward links
  timeAdjacentUrls: string[]; // both directions; no need to distinguish
  disableTagPropagation?: boolean;
  lastWindowId?: number; // For grouping in stash
  dueDate?: number; // For todo
  completionStatus?: 'pending' | 'completed'; // Presence signals "this is a todo"
  priority?: number; // 1=high, 2=medium, 3=low
  creationDate?: number; // ms timestamp
  embedding?: number[];
  embeddingErrored?: boolean;
  titleFetchFailed?: boolean; // set after a failed/timeout title fetch; skipped by bulk sweep
  // flagged?: boolean;
}

export interface AutoTag {
  name: string;
  inferenceStrength: number;
}

export interface Tag {
  name: string;
  last_active?: number;
  humanTags?: string[]; // including "_stash_", "_todo_", "_flag_", "_archive_"
}

export interface LogEntry {
  id?: number;
  level: 'info' | 'warn' | 'error' | 'debug';
  message: string;
  context: string;
  timestamp: number;
  timestampString?: string;
  memoryUsage?: number;
}

export class TabvanaDexie extends Dexie {
  // Declare types for Dexie tables
  prefs!: Table<Pref>;
  pages!: Table<DBPage, string>;
  tags!: Table<Tag>;
  operations!: Table<Operation, number>;
  progress!: Table<Progress, string>;
  logs!: Table<LogEntry, number>;
  completedProgress!: Table<CompletedProgress, number>;

  constructor() {
    super('tabvana');
    this.version(1).stores({
      prefs: 'name', // Primary key and indexed props
      pages: 'url, *humanTags',
      tags: 'name',
    });
    // 2. Add intermediate version and copy table to temp table, deleting original version.
    this.version(2)
      .stores({
        prefs: 'name', // Primary key and indexed props
        pages: null,
        pagesTmp: 'url, *humanTags',
        tags: 'name',
      })
      .upgrade(async (tx) => {
        console.log('Upgrading to version 2');
        const pages: DBPage[] = await tx.table('pages').toArray();
        const pagesByNormalizedUrl = _.groupBy(pages, (page) => normalizeUrl(page.url).normalized);
        const result: DBPage[] = [];
        for (const [normalizedUrl, mergePages] of Object.entries(pagesByNormalizedUrl)) {
          result.push(merge(normalizedUrl, mergePages));
        }
        
        // Batch bulkAdd to prevent call stack size exceeded errors
        const batchSize = 500;
        for (let i = 0; i < result.length; i += batchSize) {
            const batch = result.slice(i, i + batchSize);
            await tx.table('pagesTmp').bulkAdd(batch);
        }
        console.log('Done upgrading to version 2');
      });
    // 3. Copy table to new pages table, deleting temp table:
    this.version(3)
      .stores({
        prefs: 'name', // Primary key and indexed props
        pages: 'url, *humanTags',
        tags: 'name',
      })
      .upgrade(async (tx) => {
        console.log('Upgrading to version 3');
        const pages = await tx.table('pagesTmp').toArray();
        const batchSize = 500;
        for (let i = 0; i < pages.length; i += batchSize) {
            const batch = pages.slice(i, i + batchSize);
            await tx.table('pages').bulkAdd(batch);
        }
      })
      .upgrade((tx) => {
        console.log('Upgrading to version 3, part 2');
        return tx
          .table('pages')
          .toCollection()
          .modify((page: DBPage) => {
            page.originalUrl = page.url;
            page.url = normalizeUrl(page.url).normalized;
            page.openerUrls = page.openerUrls.map(normalizeUrl).map((url) => url.normalized);
            page.openedUrls = page.openedUrls.map(normalizeUrl).map((url) => url.normalized);
          });
      });
    this.version(7)
      .stores({
        prefs: 'name', // Primary key and indexed props
        pages: 'url, *humanTags, lastAccess, domainSortKey', // lastAccess was a typo!?
        tags: 'name',
      })
      .upgrade((tx) => {
        console.log('Upgrading to version 7');
        var count = 0;
        const result = tx
          .table('pages')
          .toCollection()
          .modify((page: DBPage) => {
            count += 1;
            if (count % 100 === 0) {
              console.log('Processed ', count);
            }
            const nurl = normalizeUrl(page.url);
            // page.url = nurl.normalized;  // may as well renormalize
            page.domainSortKey = nurl.domainSortKey;
            // page.lastActive = page.accessTimes[page.accessTimes.length - 1];
          });
        console.log('Done.  Processed ', count);
        return result;
      });
    this.version(8)
      .stores({
        prefs: 'name', // Primary key and indexed props
        pages: 'url, *humanTags, lastAccess, domainSortKey', // lastAccess was a typo!?
        tags: 'name',
      })
      .upgrade((tx) => {
        console.log('Upgrading to version 8');
      });
    this.version(9)
      .stores({
        prefs: 'name', // Primary key and indexed props
        pages: 'url, *humanTags, domainSortKey, lastActiveDesc, hotnessDesc',
        tags: 'name',
      })
      .upgrade((tx) => {
        console.log('Upgrading to version 9');
        // refreshHotness();
        var count = 0;
        const result = tx
          .table('pages')
          .toCollection()
          .modify((page: DBPage) => {
            count += 1;
            if (count % 100 === 0) {
              console.log('Processed ', count);
            }
            page.lastActiveDesc = -page.accessTimes[page.accessTimes.length - 1];
            page.hotnessDesc = -page.accessTimes.length;
          });
        console.log('Done.  Processed ', count);
        return result;
      });
    this.version(10).stores({
      prefs: 'name',
      pages: 'url, *humanTags, domainSortKey, lastActiveDesc, hotnessDesc',
      tags: 'name',
      operations: '++id, status, type, createdAt',
    });
    this.version(11).stores({
      prefs: 'name',
      pages: 'url, *humanTags, domainSortKey, lastActiveDesc, hotnessDesc',
      tags: 'name',
      operations: '++id, status, type, createdAt',
      progress: 'id, source, type, current, total, updatedAt',
    });
    this.version(12).stores({
        prefs: 'name',
        pages: 'url, *humanTags, domainSortKey, lastActiveDesc, hotnessDesc', // No change to index needed for embeddings usually, not querying BY embedding directly yet
        tags: 'name',
        operations: '++id, status, type, createdAt',
        progress: 'id, source, type, current, total, updatedAt',
    }).upgrade(tx => {
        // No Op upgrade for now, just future proofing if we want to index?
        // Actually we don't need to index vectors in Dexie usually unless we use a specialized index.
        // But we need to make sure the field is allowed/aware if using typescript interface.
    });
    this.version(13).stores({
        prefs: 'name',
        pages: 'url, *humanTags, domainSortKey, lastActiveDesc, hotnessDesc',
        tags: 'name',
        progress: 'id, source, type, current, total, updatedAt',
        operations: '++id, status, type, createdAt, [type+payloadHash]'
    }).upgrade(async tx => {
         // calculate hash for existing pending operations
         await tx.table('operations').toCollection().modify((op: Operation) => {
             if (!op.payloadHash && op.payload !== undefined) {
                 op.payloadHash = JSON.stringify(op.payload);
             }
         });
    });
    this.version(14).stores({
        prefs: 'name',
        pages: 'url, *humanTags, domainSortKey, lastActiveDesc, hotnessDesc',
        tags: 'name',
        progress: 'id, source, type, current, total, updatedAt',
        operations: '++id, status, type, createdAt, [type+payloadHash]',
        logs: '++id, level, timestamp'
    });
    this.version(15).stores({
        prefs: 'name',
        pages: 'url, *humanTags, domainSortKey, lastActiveDesc, hotnessDesc',
        tags: 'name',
        progress: 'id, source, type, current, total, updatedAt',
        operations: '++id, status, type, createdAt, [type+payloadHash]',
        logs: '++id, level, timestamp',
        completedProgress: '++id, completedAt'
    });
    // Add visible index so updateFromBrowser can efficiently find active pages
    // without a full table scan.
    this.version(16).stores({
        prefs: 'name',
        pages: 'url, *humanTags, domainSortKey, lastActiveDesc, hotnessDesc, visible',
        tags: 'name',
        progress: 'id, source, type, current, total, updatedAt',
        operations: '++id, status, type, createdAt, [type+payloadHash]',
        logs: '++id, level, timestamp',
        completedProgress: '++id, completedAt'
    });
    // Add [status+priority] compound index so processQueue can fetch pending
    // operations pre-sorted by priority without a JS-side sort.
    this.version(17).stores({
        prefs: 'name',
        pages: 'url, *humanTags, domainSortKey, lastActiveDesc, hotnessDesc, visible',
        tags: 'name',
        progress: 'id, source, type, current, total, updatedAt',
        operations: '++id, status, type, createdAt, [type+payloadHash], [status+priority]',
        logs: '++id, level, timestamp',
        completedProgress: '++id, completedAt'
    });
    // Add completionStatus index to pages for todo list queries.
    this.version(18).stores({
        prefs: 'name',
        pages: 'url, *humanTags, domainSortKey, lastActiveDesc, hotnessDesc, visible, completionStatus',
        tags: 'name',
        progress: 'id, source, type, current, total, updatedAt',
        operations: '++id, status, type, createdAt, [type+payloadHash], [status+priority]',
        logs: '++id, level, timestamp',
        completedProgress: '++id, completedAt'
    });
  }
}

export interface Operation {
  id?: number;
  type: 'cleanup' | 'stash' | 'renormalize' | 'generateEmbedding' | 'fetchMissingTitle' | 'cleanupGraphRefs';
  payload: any;
  payloadHash?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  createdAt: number;
  error?: string;
  priority?: number;
}

export interface Progress {
  id: string; // unique ID for the task instance
  source: string; // e.g. 'background' or 'window-<id>'
  type: string; // e.g. 'renormalize'
  current: number;
  total: number;
  updatedAt: number;
}

export interface CompletedProgress {
  id?: number;
  originalId: string;
  source: string;
  type: string;
  total: number;
  completedAt: number;
}

export const db = new TabvanaDexie();

export async function renormalizeAll(limit?: number, batchSize: number = 100, onProgress?: (current: number, total: number) => void) {
  var count = 0;
  const renormalizeAllStart = performance.now();
  const allUrls = await db.pages.toCollection().primaryKeys();

  if (onProgress) {
      onProgress(0, allUrls.length);
  }

  // process allUrls in batches
  for (let i = 0; i < allUrls.length; i += batchSize) {
    if (limit && i >= limit) {
      break;
    }
    // Only lock db.pages — progress writes happen outside this transaction, and
    // logger writes use Dexie.ignoreTransaction, so neither db.progress nor db.logs
    // need to be in scope here. Unnecessarily locking them held exclusive write locks
    // that blocked progress updates from the similarity worker and backup reads.
    await db.transaction('rw', [db.pages], async () => {
      const renormalizeBatchStart = performance.now();
      const batch = allUrls.slice(i, i + batchSize);
      // Process sequentially to avoid race conditions during merge
      for (const url of batch) {
        try {
            await renormalizeOne(url);
        } catch (e) {
            logger.error('Renormalize failed for', url, e);
        }
      }

      logger.debug(`Processed batch ${i / batchSize + 1}: ${batch.length} urls in ${(performance.now() - renormalizeBatchStart).toFixed(0)} ms`);
    });

    if (onProgress) {
        onProgress(Math.min(i + batchSize, allUrls.length), allUrls.length);
    }
  }

  logger.info(`renormalizeAll: ${(performance.now() - renormalizeAllStart).toFixed(0)} ms`);
}

export async function renormalizeOne(url: string) {
  // await db.transaction('rw', db.pages, async () => {
  const page = await db.pages.get(url);
  if (!page) return;

  if(!page.url) {
    logger.error('renormalizeOne: page.url is empty', {url, page});
    return;
  }

  if(page.url !== url) {
    logger.warn('renormalizeOne: page.url does not match key', {url, pageUrl: page.url});
  }

  const nurl = normalizeUrl(page.url);

  if(shouldIgnore(page.url) || shouldIgnore(nurl.normalized)) {
    logger.info('renormalizeOne: ignoring url, deleting', nurl.normalized);
    await db.pages.delete(page.url);
    await db.pages.delete(nurl.normalized);
    return;
  }

  var newPage = { ...page };
  newPage.url = nurl.normalized;
  newPage.domainSortKey = nurl.domainSortKey;
  newPage.openedUrls = _.uniq((newPage.openedUrls || []).map(normalizeUrl).map((url) => url.normalized));
  newPage.openerUrls = _.uniq((newPage.openerUrls || []).map(normalizeUrl).map((url) => url.normalized));
  newPage.timeAdjacentUrls = _.uniq(
    (newPage.timeAdjacentUrls || []).map(normalizeUrl).map((url) => url.normalized)
  );
  newPage.humanTags = _.uniq(newPage.humanTags || []);
  newPage.humanRejectedTags = _.uniq(newPage.humanRejectedTags || []);

  if (newPage.url !== page.url) {
    try {
        // URL changed, so we need to delete the old entry and add the new one
        await db.pages.delete(page.url);
        const existingPage = await db.pages.get(newPage.url);
        if (existingPage) {
          // Merge with existing page
          newPage = merge(newPage.url, [existingPage, newPage]);
        }
        await db.pages.put(newPage);
    } catch (e: any) {
        if (e.name === 'QuotaExceededError') {
            logger.error(`[DB] QuotaExceededError while migrating URL: ${newPage.url}`);
        } else {
            logger.error(`[DB] Error while migrating URL list: ${newPage.url}`, e);
        }
        throw e;
    }
  } else {
    try {
        // URL didn't change, so we can just update the existing entry
        await db.pages.put(newPage);
    } catch (e: any) {
        if (e.name === 'QuotaExceededError') {
             logger.error(`[DB] QuotaExceededError while updating URL: ${newPage.url}`);
        } else {
             logger.error(`[DB] Error while updating URL: ${newPage.url}`, e);
        }
        throw e;
    }
  }

  //);
  // console.timeEnd('renormalizeOne');
}

function merge(normalizedUrl: string, mergePages: DBPage[]): DBPage {
  const result = { ...mergePages[0] };
  result.url = normalizedUrl;
  result.originalUrl = result.originalUrl || result.url;

  result.openerUrls = _.uniq(result.openerUrls.map(normalizeUrl).map((url) => url.normalized));
  result.openedUrls = _.uniq(result.openedUrls.map(normalizeUrl).map((url) => url.normalized));
  result.timeAdjacentUrls = _.uniq(
    result.timeAdjacentUrls?.map(normalizeUrl).map((url) => url.normalized) || []
  );

  logger.debug('Merging', mergePages.length, 'pages for', normalizedUrl);

  for (const page of mergePages.slice(1)) {
    // Pick various values from the page with the most recent access time
    if (
      page.accessTimes[page.accessTimes.length - 1] >
      result.accessTimes[result.accessTimes.length - 1]
    ) {
      result.title = page.title;
      result.favIconUrl = page.favIconUrl;
      result.visible = page.visible;
      result.originalUrl = page.originalUrl;
    }
    result.accessTimes = _.uniq([...result.accessTimes, ...page.accessTimes].toSorted());
    result.lastActiveDesc = -result.accessTimes[result.accessTimes.length - 1];
    result.hotnessDesc = -result.accessTimes.length;
    result.humanTags = _.uniq([...result.humanTags, ...page.humanTags]);
    result.humanRejectedTags = _.uniq([...result.humanRejectedTags, ...page.humanRejectedTags]);
    result.autoTags = _.uniqBy([...result.autoTags, ...page.autoTags], (autoTag) => autoTag.name);

    result.openerUrls = _.uniq([
      ...result.openerUrls,
      ...page.openerUrls.map(normalizeUrl).map((url) => url.normalized),
    ]);
    result.openedUrls = _.uniq([
      ...result.openedUrls,
      ...page.openedUrls.map(normalizeUrl).map((url) => url.normalized),
    ]);
    result.timeAdjacentUrls = _.uniq([
      ...result.timeAdjacentUrls,
      ...(page.timeAdjacentUrls || []).map(normalizeUrl).map((url) => url.normalized),
    ]);

    result.disableTagPropagation = result.disableTagPropagation || page.disableTagPropagation;
  }
  return result;
}

/** Check if storage is persisted already.
  @returns {Promise<boolean>} Promise resolved with true if current origin is
  using persistent storage, false if not, and undefined if the API is not
  present.
*/
async function isStoragePersisted() {
  return (await navigator.storage) && navigator.storage.persisted
    ? navigator.storage.persisted()
    : undefined;
}

/** Tries to convert to persisted storage.
  @returns {Promise<boolean>} Promise resolved with true if successfully
  persisted the storage, false if not, and undefined if the API is not present.
*/
async function persist() {
  return (await navigator.storage) && navigator.storage.persist
    ? navigator.storage.persist()
    : undefined;
}

/** Queries available disk quota.
  @see https://developer.mozilla.org/en-US/docs/Web/API/StorageEstimate
  @returns {Promise<{quota: number, usage: number}>} Promise resolved with
  {quota: number, usage: number} or undefined.
*/
async function showEstimatedQuota() {
  return (await navigator.storage) && navigator.storage.estimate
    ? navigator.storage.estimate()
    : undefined;
}

/** Tries to persist storage without ever prompting user.
  @returns {Promise<string>}
    "never" In case persisting is not ever possible. Caller don't bother
      asking user for permission.
    "prompt" In case persisting would be possible if prompting user first.
    "persisted" In case this call successfully silently persisted the storage,
      or if it was already persisted.
*/
async function tryPersistWithoutPromptingUser() {
  if (!navigator.storage || !navigator.storage.persisted) {
    return 'never';
  }
  let persisted = await navigator.storage.persisted();
  if (persisted) {
    return 'persisted';
  }
  if (!navigator.permissions || !navigator.permissions.query) {
    return 'prompt'; // It MAY be successful to prompt. Don't know.
  }
  const permission = await navigator.permissions.query({
    name: 'persistent-storage',
  });
  if (permission.state === 'granted') {
    persisted = await navigator.storage.persist();
    if (persisted) {
      return 'persisted';
    } else {
      throw new Error('Failed to persist');
    }
  }
  if (permission.state === 'prompt') {
    return 'prompt';
  }
  return 'never';
}

async function initStoragePersistence() {
  const persist = await tryPersistWithoutPromptingUser();
  switch (persist) {
    case 'never':
      logger.warn('Not possible to persist storage');
      break;
    case 'persisted':
      logger.info('Successfully persisted storage silently');
      break;
    case 'prompt':
      logger.info('Not persisted, but we may prompt user when we want to.');
      break;
  }
}

initStoragePersistence();
